import { Box, Spinner, useToast } from "@chakra-ui/react";
import { UploadArea } from "@root/components/material-upload/material-upload";
import { useTranslation } from "react-i18next";
import { useEffect, useRef, useState } from "react";
import {
  FILE_TYPE,
  FILES_PER_PAGE,
  FINISHING_MATERIAL_SUBTYPE,
} from "@root/constants";
import { createDocumentWithProgress } from "@root/store/thunks/documentsActions";
import { useDispatch } from "react-redux";
import FilesList from "./FilesList";
import { ProjectsService } from "@root/services/projects";
import { getCustomFileName } from "@root/utils";
import { useNotification } from "@root/hooks/useNotification";
import { Paginate } from "../pagination";

interface DrawingFilesProps {
  onFileSelect: (file: any) => void;
  selectedFile: any;
}

const DrawingFiles: React.FC<DrawingFilesProps> = ({
  onFileSelect,
  selectedFile,
}) => {
  const dispatch = useDispatch();
  const toast = useToast();
  const { t } = useTranslation("common");
  const [drawing, setDrawing] = useState<any>(null);
  const [getDrawingLoading, setGetDrawingLoading] = useState(false);
  const [pageNo, setPageNo] = useState(1);
  const [fileCount, setFileCount] = useState(0);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const projectService = new ProjectsService();
  const { showNotification } = useNotification();

  const fetchDocuments = async (payload: any) => {
    setGetDrawingLoading(true);
    try {
      const res = await projectService.fetchDocuments(payload);
      setDrawing(res?.data?.data);
      setFileCount(res?.data?.data?.count);

      // Auto-select first file if available, otherwise clear selection
      if (res?.data?.data?.rows?.length > 0) {
        onFileSelect(res.data.data.rows?.[0]);
      } else {
        onFileSelect(null); // ← Add this
      }
    } catch (error: unknown) {
      console.warn(error);
    } finally {
      setGetDrawingLoading(false);
    }
  };

  const payload = {
    type: FILE_TYPE.FINISHING_MATERIAL_REQUEST,
    sub_type: FINISHING_MATERIAL_SUBTYPE.DRAWING_FILE,
    limit: FILES_PER_PAGE,
    page: pageNo,
  };

  const handlePageChange = (newPage: number) => {
    setPageNo(newPage);
    fetchDocuments({
      type: FILE_TYPE.FINISHING_MATERIAL_REQUEST,
      sub_type: FINISHING_MATERIAL_SUBTYPE.DRAWING_FILE,
      limit: FILES_PER_PAGE,
      page: newPage,
    });
  };

  useEffect(() => {
    fetchDocuments(payload);
  }, []);

  const handleDrawingFiles = (file: File, cb: (done: boolean) => void) => {
    setIsUploading(true);
    setUploadProgress(0);
    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();

    const formData = new FormData();
    formData.append("file", file);
    formData.append("type", FILE_TYPE.FINISHING_MATERIAL_REQUEST.toString());
    formData.append(
      "sub_type",
      FINISHING_MATERIAL_SUBTYPE.DRAWING_FILE.toString()
    );
    // const customFileName = getCustomFileName(file.name);
    formData.append("name", file.name);

    dispatch(
      createDocumentWithProgress({
        formData,
        signal: abortControllerRef.current.signal,
        onProgress: (progress: number) => {
          setUploadProgress(progress);
        },
        onSuccess: (res: any) => {
          setUploadProgress(100);
          setIsUploading(false);
          abortControllerRef.current = null;
          fetchDocuments(payload);
          cb(true);
        },
        onError: () => {
          setUploadProgress(0);
          setIsUploading(false);
          abortControllerRef.current = null;
          toast({
            title: t("upload_document.upload_failed"),
            status: "error",
          });
          cb(false);
        },
      })
    );
  };

  const onCancelUpload = () => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    setIsUploading(false);
    setUploadProgress(0);
    showNotification({
      type: "success",
      message: t("upload_document.upload_canceled"),
    });
  };

  const renderFilesList = () => {
    if (getDrawingLoading) {
      return (
        <Box marginTop={"120px"} textAlign={"center"}>
          <Spinner />
        </Box>
      );
    }
    return (
      <Box display="flex" flexDirection="column">
        {/* Main content area (files) */}
        <Box flex="1" overflowY="auto">
          <FilesList
            files={drawing?.rows}
            onFileSelect={onFileSelect}
            selectedFile={selectedFile}
            fileType="drawing"
            onRefresh={() => fetchDocuments(payload)}
          />
        </Box>

        {/* Pagination directly after files */}
        <Box bg="white" p={4} display="flex" justifyContent="center">
          <Paginate
            page={pageNo}
            count={fileCount}
            pageSize={FILES_PER_PAGE}
            onPageChange={handlePageChange}
          />
        </Box>
      </Box>
    );
  };

  return (
    <Box
      flex="1"
      border="2px solid #CDD6DC99"
      borderRadius="8px"
      padding="24px"
    >
      <UploadArea
        title={t("materialCamparison.uploadDrawing")}
        handleFiles={handleDrawingFiles}
        progress={uploadProgress}
        isUploading={isUploading}
        onCancelUpload={onCancelUpload}
        validatePdfPages={false}
        onPdfValidationError={(message) => {
          showNotification({
            type: "error",
            message: message,
          });
        }}
        onFileSizeError={(message) => {
          showNotification({
            type: "error",
            message: message,
          });
        }}
      />
      {renderFilesList()}
    </Box>
  );
};

export default DrawingFiles;
